export const dynamic = 'force-dynamic';
import { FC, useMemo } from 'react';
import { Text, View } from '@react-pdf/renderer';

import { AssetCategoryToText } from '@/entities/assets';
import { AssetItem, IWBeneficiary } from '@/entities/interwills';
import { getCountryName } from '@/shared/constants/countries-list';
import { AssetCategory } from '@/shared/lib/sj-orm/models/asset.dto';
import { dates } from '@/shared/utils/dates';
import { useTranslation } from '@/shared/utils/i18n/useTranslation';

import { useGetDocumentCategory, useWithPlaceholder } from './hooks/';
import { AssetDetails } from './AssetDetails';

import { styles } from './styles';

type DisplayAssetProps = {
  asset: AssetItem;
  testator: IWBeneficiary;
  index: number;
  itemCounters: Record<string, number>;
  isDraft?: boolean;
};

export const DisplayAsset: FC<DisplayAssetProps> = ({
  asset,
  testator,
  index,
  itemCounters,
  isDraft,
}) => {
  const { t } = useTranslation('widgets/pdfs', {
    keyPrefix: 'lastWill',
  });
  const { t: ta } = useTranslation(['assets', 'forms_assets']);

  const withPlaceholder = useWithPlaceholder();
  const getDocumentCategory = useGetDocumentCategory();

  if (!itemCounters[asset.category]) {
    itemCounters[asset.category] = 0;
  }

  const ownerIdDoc = useMemo(() => {
    const documents = [testator.primaryCitizenship, ...(testator.otherCitizenships ?? [])];

    return documents.find((item) => item?.id === asset.ownerId);
  }, [testator, asset]);

  return (
    <>
      <View style={{ ...styles.row, ...styles.subTitle }} wrap={false}>
        <View style={styles.listNumber}>
          <Text>4.{index + 1}</Text>
        </View>
        <Text>
          {ta(AssetCategoryToText[asset.category])} {++itemCounters[asset.category]}
        </Text>
      </View>

      <View style={{ paddingLeft: 36 }}>
        <AssetDetails asset={asset} isDraft={isDraft} />
      </View>

      {asset.proofOfOwnership && (
        <View style={{ paddingLeft: 36 }}>
          <Text>
            {t('assetsBlock.proofOfOwnership')} {t('assetsBlock.documentNumber')}{' '}
            {withPlaceholder(asset.proofOfOwnership.number, 'docNumberProff')},{' '}
            {t('assetsBlock.dated')}{' '}
            {withPlaceholder(
              dates.formatLong(asset.proofOfOwnership.startDate as unknown as string),
              'docDateProff',
            )}
            .
          </Text>
        </View>
      )}

      {ownerIdDoc && (
        <View style={{ paddingLeft: 36 }}>
          <Text>
            {t('assetsBlock.ownerId')}{' '}
            {withPlaceholder(getCountryName(ownerIdDoc.country), 'country')},{' '}
            {withPlaceholder(getDocumentCategory(ownerIdDoc.documentType), 'docCategory')}:{' '}
            {t('number').toLowerCase()} {withPlaceholder(ownerIdDoc.number, 'docNumber')},{' '}
            {t('citizenship.dateOfIssue')}{' '}
            {withPlaceholder(
              ownerIdDoc.issueDate && dates.formatLong(ownerIdDoc.issueDate),
              'docStartDate',
            )}
            .
          </Text>
        </View>
      )}

      {[
        AssetCategory.LAND,
        AssetCategory.RESIDENTIAL_REAL_ESTATE,
        AssetCategory.COMMERCIAL_REAL_ESTATE,
      ].includes(asset.category) && (
        <View style={{ paddingLeft: 36 }}>
          <Text>{t('assetsBlock.landOfThePlot')}</Text>
        </View>
      )}

      <View style={{ paddingLeft: 36 }}>
        <Text>
          {t('assetsBlock.reffersAs', {
            assetName: ta(AssetCategoryToText[asset.category]),
            number: itemCounters[asset.category],
          })}
        </Text>
      </View>
    </>
  );
};
